import { useState, useEffect } from 'react';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CreateAdditionalRecipients,
  GetAdditionalRecipients,
  DeleteAdditionalRecipient,
  UpdateAdditionalRecipient,
} from '../../../../api/MyIMO';
import AddRecipientForm from './Components/AddRecipientForm';
import RecipientTable from './Components/RecipientTable';
import { Snackbar } from '../../../../components/Global/Snackbar/Snackbar';
import GenericDialog from '../../../../components/Dialog/GenericDialog';
import { validateEmail } from '../../../../components/Utilities/HelperMethods';

const Notifications = ({ user }) => {
  const { producerID } = user;
  const queryClient = useQueryClient();

  const [recipients, setRecipients] = useState([]);
  const [newRecipient, setNewRecipient] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [inputError, setInputError] = useState('');
  const [editError, setEditError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [recipientToDelete, setRecipientToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const {
    isLoading,
    data: recipientsData,
    isError: recipientsError,
  } = useQuery({
    queryKey: ['getAdditionalRecipients', producerID],
    queryFn: () => GetAdditionalRecipients(producerID)(),
    enabled: !!producerID,
  });

  useEffect(() => {
    if (recipientsData?.recipients) {
      setRecipients(recipientsData.recipients);
    }
  }, [recipientsData]);

  useEffect(() => {
    if (recipientsError) {
      setRecipients([]);
    }
  }, [recipientsError]);

  const { mutate } = useMutation({
    mutationFn: ({ lookupIds, emailsToAdd }) =>
      CreateAdditionalRecipients({
        LookupIds: lookupIds,
        Recipients: emailsToAdd,
      })(),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['getAdditionalRecipients', producerID] });
      setSnackbar({
        open: true,
        message: response?.Message || 'Recipients updated successfully',
        severity: 'success',
      });
      setNewRecipient('');
      setEditingIndex(null);
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Failed to update recipients',
        severity: 'error',
      });
    },
  });

  const { mutate: deleteMutate } = useMutation({
    mutationFn: (ID) => DeleteAdditionalRecipient({ ID })(),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['getAdditionalRecipients', producerID] });
      setSnackbar({
        open: true,
        message: response?.Message || 'Recipients deleted successfully',
        severity: 'success',
      });
      setNewRecipient('');
      setEditingIndex(null);
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Failed to delete recipients',
        severity: 'error',
      });
    },
  });

  const { mutate: updateMutate } = useMutation({
    mutationFn: (body) => UpdateAdditionalRecipient(body)(),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['getAdditionalRecipients', producerID] });
      setSnackbar({
        open: true,
        message: response?.Message || 'Recipient updated successfully',
        severity: 'success',
      });
      setNewRecipient('');
      setEditingIndex(null);
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Failed to update recipient',
        severity: 'error',
      });
    },
  });

  const handleAddRecipient = () => {
    setInputError('');

    if (!newRecipient.trim()) {
      setInputError('Please enter an email address');
      return;
    }

    const emailsToAdd = newRecipient
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e);

    const invalidEmails = emailsToAdd.filter((email) => !validateEmail(email));
    if (invalidEmails.length > 0) {
      setInputError(`Invalid email address: ${invalidEmails[0]}`);
      return;
    }

    // Check for duplicates by comparing recipientEmail property
    const duplicates = emailsToAdd.filter((email) =>
      recipients.some((r) => r.recipientEmail === email)
    );
    if (duplicates.length > 0) {
      setInputError(`Recipient already exists: ${duplicates[0]}`);
      return;
    }

    // Only pass the newly added emails to the API
    mutate({
      lookupIds: [producerID],
      emailsToAdd: emailsToAdd,
    });
  };

  const handleDeleteRecipient = (recipient) => {
    deleteMutate(recipient);
  };

  const handleDeleteClick = (ID) => {
    setRecipientToDelete(ID);
    setDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (recipientToDelete !== null) {
      handleDeleteRecipient(recipientToDelete);
    }
    setDialogOpen(false);
    setRecipientToDelete(null);
  };

  const handleStartEdit = (index) => {
    setEditingIndex(index);
    setEditValue(recipients[index].recipientEmail);
    setEditError('');
  };

  const handleSaveEdit = () => {
    setEditError('');

    if (!editValue.trim()) {
      setEditError('Email address cannot be empty');
      return;
    }
    if (!validateEmail(editValue)) {
      setEditError('Please enter a valid email address');
      return;
    }

    // Check if the new email already exists (excluding the current one being edited)
    const isDuplicate = recipients.some(
      (r, idx) => idx !== editingIndex && r.recipientEmail === editValue
    );
    if (isDuplicate) {
      setEditError('This recipient already exists');
      return;
    }

    const recipientToUpdate = recipients[editingIndex];

    updateMutate({
      ID: recipientToUpdate.id,
      NewRecipient: editValue,
    });
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditValue('');
    setEditError('');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleInputChange = (e) => {
    setNewRecipient(e.target.value);
    if (inputError) {
      setInputError('');
    }
  };

  const handleEditChange = (e) => {
    setEditValue(e.target.value);
    if (editError) {
      setEditError('');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <GenericDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Recipient"
        content="Are you sure you want to delete this recipient? This action cannot be undone."
        cancelText="Cancel"
        confirmText="Delete"
        confirmColor="error"
      />
      <Typography variant="h5" gutterBottom>
        Notification Recipients
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage email addresses that will receive notifications
      </Typography>
      <AddRecipientForm
        value={newRecipient}
        onChange={handleInputChange}
        onAdd={handleAddRecipient}
        error={inputError}
      />
      {isLoading ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={33} sx={{ color: 'black' }} />
        </Paper>
      ) : recipients.length > 0 ? (
        <RecipientTable
          recipients={recipients}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          editingIndex={editingIndex}
          editValue={editValue}
          editError={editError}
          onStartEdit={handleStartEdit}
          onSaveEdit={handleSaveEdit}
          onCancelEdit={handleCancelEdit}
          onDelete={handleDeleteClick}
          onEditChange={handleEditChange}
        />
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No recipients added yet. Add your first recipient above.
          </Typography>
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        status={snackbar.severity}
        onClose={handleCloseSnackbar}
      />
    </Box>
  );
};

export default Notifications;
